"use client";

import { useDeferredValue, useState } from "react";

import type { StoryRecord, TagDefinition } from "../../_types/content";
import StoryLinkCard from "../storefront/StoryLinkCard";
import {
	FilterDropdown,
	getGroupedTagOptions,
} from "../storefront/ui/GroupedTagFilters";
import { ContentGrid } from "../storefront/ui/Templates";

interface CommunityWallFilters {
	locations: string[];
	tagIdsByType: Record<string, string[]>;
}

const EMPTY_FILTERS: CommunityWallFilters = {
	locations: [],
	tagIdsByType: {},
};

function getStoryLocation({ locationLabel, user }: StoryRecord) {
	return locationLabel ?? user.profile.location.region;
}

export function getCommunityWallFilterOptions(
	records: readonly StoryRecord[],
	tags: readonly TagDefinition[],
) {
	const tagGroups = getGroupedTagOptions(
		new Set(records.flatMap(({ story }) => story.tagIds)),
		tags,
	);
	const locations = [
		...new Set(records.map((record) => getStoryLocation(record))),
	].toSorted();

	return { locations, tagGroups };
}

export function filterCommunityWallStories(
	records: readonly StoryRecord[],
	filters: CommunityWallFilters,
) {
	return records.filter((record) => {
		const matchesLocation =
			filters.locations.length === 0 ||
			filters.locations.includes(getStoryLocation(record));
		const matchesTags = Object.values(filters.tagIdsByType).every(
			(selectedTagIds) =>
				selectedTagIds.length === 0 ||
				selectedTagIds.some((tagId) => record.story.tagIds.includes(tagId)),
		);

		return matchesLocation && matchesTags;
	});
}

export default function CommunityWallCatalog({
	records,
	tags,
}: {
	records: readonly StoryRecord[];
	tags: readonly TagDefinition[];
}) {
	const [filters, setFilters] = useState(EMPTY_FILTERS);
	const deferredFilters = useDeferredValue(filters);
	const filteredRecords = filterCommunityWallStories(records, deferredFilters);
	const { locations, tagGroups } = getCommunityWallFilterOptions(records, tags);
	const selectedCount =
		filters.locations.length +
		Object.values(filters.tagIdsByType).reduce(
			(total, selectedTagIds) => total + selectedTagIds.length,
			0,
		);

	function toggleLocation(location: string) {
		setFilters((current) => ({
			...current,
			locations: current.locations.includes(location)
				? current.locations.filter((value) => value !== location)
				: [...current.locations, location],
		}));
	}

	function toggleTag(type: string, tagId: string) {
		setFilters((current) => {
			const selectedTagIds = current.tagIdsByType[type] ?? [];
			return {
				...current,
				tagIdsByType: {
					...current.tagIdsByType,
					[type]: selectedTagIds.includes(tagId)
						? selectedTagIds.filter((value) => value !== tagId)
						: [...selectedTagIds, tagId],
				},
			};
		});
	}

	return (
		<section aria-label="Community Wall" className="pt-10 lg:pt-14">
			<div className="border-y border-burgundy/25 py-5">
				<div className="flex flex-wrap items-center gap-3">
					<FilterDropdown
						label="Location"
						options={locations.map((location) => ({
							value: location,
							label: location,
						}))}
						selectedValues={filters.locations}
						onToggle={toggleLocation}
					/>
					{tagGroups.map((group) => (
						<FilterDropdown
							key={group.type}
							label={group.label}
							options={group.options.map((tag) => ({
								value: tag.id,
								label: tag.label,
							}))}
							selectedValues={filters.tagIdsByType[group.type] ?? []}
							onToggle={(tagId) => toggleTag(group.type, tagId)}
						/>
					))}
					<p className="ml-auto text-sm font-[700] text-burgundy">
						{filteredRecords.length}{" "}
						{filteredRecords.length === 1 ? "story" : "stories"}
					</p>
					{selectedCount > 0 ? (
						<button
							type="button"
							onClick={() => setFilters(EMPTY_FILTERS)}
							className="border-b-2 border-burgundy font-[700] text-burgundy"
						>
							Reset filters
						</button>
					) : null}
				</div>
			</div>

			<div className="pt-8">
				{filteredRecords.length > 0 ? (
					<ContentGrid variant="wall">
						{filteredRecords.map((record, index) => (
							<div key={record.story.id} className="mb-7 break-inside-avoid">
								<StoryLinkCard
									{...record}
									index={index}
									presentation="related"
									hrefBase="/test/community-wall"
								/>
							</div>
						))}
					</ContentGrid>
				) : (
					<div className="border border-burgundy/25 px-6 py-16 text-center text-burgundy">
						<p className="font-neueheitz text-2xl">
							No stories match these filters.
						</p>
						<button
							type="button"
							onClick={() => setFilters(EMPTY_FILTERS)}
							className="mt-6 border-b-2 border-burgundy font-[700]"
						>
							Reset filters
						</button>
					</div>
				)}
			</div>
		</section>
	);
}
